// src/resources/reports/ReportAppPenetration/PenetrationTable.tsx
//
// "Por posto": escopo na linha, métrica na coluna (§4 do spec) — 6 métricas ×
// 3 escopos por posto ficariam ilegíveis numa coluna por número, então cada
// posto vira um grupo de 3 <tbody> linhas (No app / Fora / Total), como no
// mockup aprovado. `byStation` já vem ordenado pelo backend (share.volume desc).
import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import {
  MetricKey, METRIC_CAPTIONS, METRIC_LABELS, METRIC_ORDER, RowScope, StationRow,
  discountShareOfAppValue, fmtPct, formatMetricValue, metricSetFor, metricValueFor, shareFor,
} from './types';

const BLUE = '#3b82f6';
const GRAY = '#94a3b8';
const SUCCESS = '#16a34a';
const DANGER = '#ef4444';

const DISCOUNT_OFF_TOOLTIP = 'Desconto só existe em abastecimento identificado pelo app';

const Dot = ({ color }: { color: string }) => (
  <Box component="span" sx={{ display: 'inline-block', width: 7, height: 7, borderRadius: 999, bgcolor: color, mr: 0.75 }} />
);

const StationHeaderCell = ({ row }: { row: StationRow }) => (
  <TableCell rowSpan={3} sx={{ verticalAlign: 'top', width: 210, borderRight: '1px solid #f1f5f9' }}>
    <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>{row.name}</Typography>
    <Box sx={{ height: 7, borderRadius: 999, bgcolor: '#e2e8f0', overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${Math.min(100, (row.share.volume ?? 0) * 100)}%`, background: `linear-gradient(90deg, ${BLUE}, #06b6d4)` }} />
    </Box>
    <Typography sx={{ fontSize: 10.5, color: GRAY, mt: 0.4 }}>{fmtPct(row.share.volume, 1)} do volume no app</Typography>
  </TableCell>
);

// Legenda curta embaixo de cada escopo: "Fora do app" e "Total da pista" são
// óbvios pra quem montou o relatório, não pra quem abre pela primeira vez.
const SCOPE_CAPTION: Record<RowScope, string> = {
  [RowScope.App]: 'cliente identificado pelo aplicativo',
  [RowScope.Off]: 'abasteceu sem passar pelo app',
  [RowScope.Total]: 'tudo que passou pela bomba',
};

const ScopeCell = ({ scope }: { scope: RowScope }) => {
  const style = scope === RowScope.App
    ? { fontWeight: 600, color: BLUE }
    : scope === RowScope.Off
      ? { fontWeight: 600, color: '#64748b' }
      : { fontWeight: 700, color: '#334155' };
  const label = scope === RowScope.App ? 'No app' : scope === RowScope.Off ? 'Fora do app' : 'Total da pista';
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      <Typography sx={{ fontSize: 12.5, ...style }}>
        {scope !== RowScope.Total && <Dot color={scope === RowScope.App ? BLUE : GRAY} />}
        {label}
      </Typography>
      <Typography sx={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.3 }}>{SCOPE_CAPTION[scope]}</Typography>
    </TableCell>
  );
};

/** Participação secundária (o `s` do mockup) de Valor/Litros/Abastecimentos e Descontos. */
const shareSecondary = (row: StationRow, metricKey: MetricKey, scope: RowScope): number | null => {
  const appShare = metricKey === MetricKey.Discounts ? discountShareOfAppValue(row) : shareFor(row, metricKey);
  if (appShare === null) return null;
  if (scope === RowScope.App) return appShare;
  // Fora do app é o complemento da mesma fatia — não se aplica a descontos
  // (a linha Fora de descontos já sai como travessão antes de chegar aqui).
  if (scope === RowScope.Off && metricKey !== MetricKey.Discounts) return 1 - appShare;
  return null;
};

const MetricCell = ({ metricKey, row, scope }: { metricKey: MetricKey; row: StationRow; scope: RowScope }) => {
  const metrics = metricSetFor(row, scope);
  const value = metricValueFor(metrics, metricKey);
  const isLiftMetric = metricKey === MetricKey.AvgTicket || metricKey === MetricKey.AvgVolume;

  if (metricKey === MetricKey.Discounts && scope === RowScope.Off) {
    return (
      <TableCell align="right">
        <Tooltip title={DISCOUNT_OFF_TOOLTIP}>
          <Box component="span" sx={{ color: '#cbd5e1', cursor: 'help' }}>—</Box>
        </Tooltip>
      </TableCell>
    );
  }

  let secondary: React.ReactNode = null;

  if (isLiftMetric && scope === RowScope.App) {
    // Ticket/volume médio não somam — em vez de participação, o ganho do app contra o fora.
    const lift = metricKey === MetricKey.AvgTicket ? row.lift.avgTicket : row.lift.avgVolume;
    if (lift !== null) {
      secondary = (
        <Typography component="span" sx={{ fontSize: 10.5, fontWeight: 700, color: lift >= 0 ? SUCCESS : DANGER, display: 'block' }}>
          {lift >= 0 ? '▲' : '▼'} {fmtPct(Math.abs(lift), 1)}
        </Typography>
      );
    }
  } else if (!isLiftMetric) {
    const share = shareSecondary(row, metricKey, scope);
    if (share !== null) {
      secondary = (
        <Typography component="span" sx={{ fontSize: 10.5, fontWeight: scope === RowScope.App ? 700 : 400, color: scope === RowScope.App ? BLUE : GRAY, display: 'block' }}>
          {fmtPct(share, 1)}
        </Typography>
      );
    }
  }

  return (
    <TableCell align="right">
      <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: scope === RowScope.Total ? 700 : 400 }}>
        {formatMetricValue(metricKey, value)}
      </Box>
      {secondary}
    </TableCell>
  );
};

const StationGroup = ({ row, highlight }: { row: StationRow; highlight?: boolean }) => (
  <TableBody>
    <TableRow sx={{ bgcolor: highlight ? '#f8fbff' : 'rgba(59,130,246,0.05)', borderTop: '2px solid #e2e8f0' }}>
      <StationHeaderCell row={row} />
      <ScopeCell scope={RowScope.App} />
      {METRIC_ORDER.map(key => <MetricCell key={key} metricKey={key} row={row} scope={RowScope.App} />)}
    </TableRow>
    <TableRow sx={{ bgcolor: highlight ? '#f8fbff' : undefined }}>
      <ScopeCell scope={RowScope.Off} />
      {METRIC_ORDER.map(key => <MetricCell key={key} metricKey={key} row={row} scope={RowScope.Off} />)}
    </TableRow>
    <TableRow sx={{ bgcolor: highlight ? '#f8fbff' : undefined, '& td': { borderBottom: '2px solid #e2e8f0', fontWeight: 700 } }}>
      <ScopeCell scope={RowScope.Total} />
      {METRIC_ORDER.map(key => <MetricCell key={key} metricKey={key} row={row} scope={RowScope.Total} />)}
    </TableRow>
  </TableBody>
);

const PenetrationTable = ({ totals, byStation }: { totals: StationRow; byStation: StationRow[] }) => (
  <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 2 }}>
    <Table size="small" sx={{ minWidth: 820 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ bgcolor: '#f8fafc' }}>Posto</TableCell>
          <TableCell sx={{ bgcolor: '#f8fafc' }}>Escopo</TableCell>
          {METRIC_ORDER.map(key => (
            <TableCell key={key} align="right" sx={{ bgcolor: '#f8fafc', textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.4, color: '#64748b' }}>
              {METRIC_LABELS[key]}
              <Typography component="div" sx={{ fontSize: 9.5, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#94a3b8', lineHeight: 1.3 }}>
                {METRIC_CAPTIONS[key]}
              </Typography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <StationGroup row={{ ...totals, name: 'Todos os postos' }} highlight />
      {byStation.map(row => <StationGroup key={row.placeId} row={row} />)}
    </Table>
  </Box>
);

export default PenetrationTable;
